import {
  CHEMISTRY_TYPES,
  EQUIPMENT_COSTING_METHODS,
  EQUIPMENT_TYPES,
  PRODUCTION_PHASES,
  TOLERANCE_MATERIAL_STATES,
  TOLERANCE_MEASURES,
  TanneryMasterKind,
} from './tannery-master.models';

/**
 * `date` stores a `yyyy-MM-dd` string, never a `Date`; `customer` stores a
 * `dbo.CustomerMaster` id and lists the customer's joined name.
 */
export type FieldType = 'text' | 'number' | 'bool' | 'select' | 'date' | 'customer';

export interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  options?: { value: string; label: string }[];
  /** Shown under the input. Use it for consequences, not for restating the label. */
  hint?: string;
  maxLength?: number;
  /** Hidden in the table when false — keeps wide masters readable. */
  inList?: boolean;
  /**
   * Decimal places for a `type: 'number'` field, read by the generic form as
   * `field.precision ?? 2` — most quantities across the six masters are
   * `DECIMAL(18,2)` or `(5,2)` and want the default. Four fields are
   * `DECIMAL(18,4)` and set `precision: 4` explicitly:
   * `hidetype.AvgWeightKg`/`AvgAreaSqft` and `stageconfig.DefaultDurationHours`/`DefaultRestDurationHours`.
   * Getting a field's own precision wrong is not cosmetic: ng-zorro's
   * `nz-input-number` fires `onChange` when its bound value's precision
   * differs from what is displayed, so opening the edit modal on a stored
   * `12.3456` under a `precision: 2` field rewrites it to `12.35` before the
   * user touches anything, and the next Save persists that rounding even if
   * only an unrelated field was corrected. Set to `0` for a true integer
   * such as `stageconfig`'s `Sequence` (`INT NOT NULL`, unique): without it,
   * the 2-decimal default rendered "3.00", stepped by 0.01, and rewrote a
   * typed "3" on save.
   */
  precision?: number;
  /** Value a brand-new row starts with, when the generic "everything false/blank" default is wrong — e.g. GradeMaster.IsSaleable defaults true server-side. */
  defaultValue?: boolean | number | string;
  /**
   * Lets a required `type: 'number'` field accept 0. Off by default because the
   * numbers the other masters require — a stage's Sequence, a machine's fill
   * ratios — are all "must be more than zero". A customer tolerance of 0% is a
   * real agreement (that customer accepts no deviation at all), so it sets this.
   */
  allowZero?: boolean;
}

export interface MasterDef {
  kind: TanneryMasterKind;
  title: string;
  subtitle: string;
  /** Singular, for buttons and modal titles. */
  singular: string;
  /** Column the list is ordered by on screen; the API already sorts. */
  fields: FieldDef[];
}

const CODE: FieldDef = {
  key: 'Code',
  label: 'Code',
  type: 'text',
  required: true,
  maxLength: 30,
  hint: 'Identifies this row to the rest of the system. Avoid changing it once in use.',
};

const NAME: FieldDef = { key: 'Name', label: 'Name', type: 'text', required: true, maxLength: 100 };
const ACTIVE: FieldDef = {
  key: 'IsActive',
  label: 'Active',
  type: 'bool',
  hint: 'Inactive rows keep their history but stop appearing in dropdowns.',
};

/**
 * Everything the master screens differ by, in one place.
 *
 * The screens are otherwise identical — list, add, edit — so they are driven
 * from these definitions rather than copied once per master. Another master is a
 * new entry here, not a new component. Equipment (Sprint 6) is gated by its own
 * `Master - Equipment` permission rather than every other kind's `Tannery -
 * Masters` — see `TANNERY_MENU_CONTRIBUTIONS` in `tannery-menu.ts`.
 *
 * Seven of the nine are Code + Name + Active rows. Sprint 21's two are not:
 * `exportprofile` is keyed by its financial year and has no active state at all,
 * and `customertolerance` is keyed by customer + state + measure, where Active
 * means the agreement still applies. Neither has a Code or a Name column, so
 * neither lists one.
 */
export const MASTER_DEFS: Record<TanneryMasterKind, MasterDef> = {
  hidetype: {
    kind: 'hidetype',
    title: 'Hide Types',
    singular: 'hide type',
    subtitle: 'Raw hide classifications and their average yields',
    fields: [
      CODE,
      NAME,
      {
        key: 'Abbreviation',
        label: 'Abbreviation',
        type: 'text',
        required: true,
        maxLength: 2,
        hint: 'Two characters. Becomes part of every article code generated from this hide type, so changing it later will not update codes already issued.',
      },
      { key: 'Category', label: 'Category', type: 'text', maxLength: 30 },
      { key: 'OriginCountry', label: 'Origin country', type: 'text', maxLength: 100, inList: false },
      { key: 'AvgWeightKg', label: 'Avg weight (kg)', type: 'number', precision: 4 },
      { key: 'AvgAreaSqft', label: 'Avg area (sqft)', type: 'number', precision: 4 },
      ACTIVE,
    ],
  },

  stageconfig: {
    kind: 'stageconfig',
    title: 'Process Stages',
    singular: 'stage',
    subtitle: 'The tannery process in order, from raw hide to dispatch',
    fields: [
      {
        key: 'Sequence',
        label: 'Sequence',
        type: 'number',
        required: true,
        precision: 0,
        hint: 'Position in the overall process. Two stages cannot share a number.',
      },
      CODE,
      NAME,
      {
        key: 'Phase',
        label: 'Phase',
        type: 'select',
        required: true,
        options: PRODUCTION_PHASES,
      },
      {
        key: 'IsGradingGate',
        label: 'Grading gate',
        type: 'bool',
        hint: 'Marks a point where output is graded before moving on. Normally the last stage of a phase.',
      },
      {
        key: 'UsesChemicals',
        label: 'Uses chemicals',
        type: 'bool',
        hint: 'This stage charges a recipe to the drum or applies a finish. Mechanical, drying, inspection and packing stages do not.',
      },
      { key: 'DefaultDurationHours', label: 'Default duration (h)', type: 'number', inList: false, precision: 4 },
      {
        key: 'DefaultRestDurationHours',
        label: 'Default rest duration (h)',
        type: 'number',
        inList: false,
        precision: 4,
        hint: 'How long material must rest after this stage before the next one starts. Leave blank if there is no rest requirement.',
      },
      {
        key: 'TemperatureRange',
        label: 'Temperature range',
        type: 'text',
        maxLength: 50,
        inList: false,
        hint: "min-max (e.g. 35-40), or a single number for a setpoint (e.g. 38). Leave blank if there is none yet.",
      },
      {
        key: 'PhRange',
        label: 'pH range',
        type: 'text',
        maxLength: 50,
        inList: false,
        hint: 'min-max (e.g. 2.8-3.2), or a single number for a setpoint. Leave blank if there is none yet.',
      },
      {
        key: 'RpmRange',
        label: 'RPM range',
        type: 'text',
        maxLength: 50,
        inList: false,
        hint: 'min-max (e.g. 8-12), or a single number for a setpoint. Leave blank if there is none yet.',
      },
      ACTIVE,
    ],
  },

  finishtype: {
    kind: 'finishtype',
    title: 'Finish Types',
    singular: 'finish type',
    subtitle: 'Surface finishes available to articles',
    fields: [
      CODE,
      NAME,
      {
        key: 'Abbreviation',
        label: 'Abbreviation',
        type: 'text',
        required: true,
        maxLength: 4,
        hint: 'Three or four characters. Leads every article code generated with this finish, so changing it later will not update codes already issued.',
      },
      ACTIVE,
    ],
  },

  tanningmethod: {
    kind: 'tanningmethod',
    title: 'Tanning Methods',
    singular: 'tanning method',
    subtitle: 'Tanning chemistries and their compliance classification',
    fields: [
      CODE,
      NAME,
      {
        key: 'Abbreviation',
        label: 'Abbreviation',
        type: 'text',
        required: true,
        maxLength: 4,
        hint: 'Two to four characters, used in article codes.',
      },
      {
        key: 'ChemistryType',
        label: 'Chemistry',
        type: 'select',
        required: true,
        options: CHEMISTRY_TYPES,
        hint: 'Drives compliance reporting and pricing.',
      },
      ACTIVE,
    ],
  },

  qctest: {
    kind: 'qctest',
    title: 'QC Tests',
    singular: 'QC test',
    subtitle: 'Laboratory tests available to specification sheets',
    fields: [
      CODE,
      NAME,
      { key: 'Unit', label: 'Unit', type: 'text', maxLength: 30 },
      { key: 'Method', label: 'Method / standard', type: 'text', maxLength: 150 },
      ACTIVE,
    ],
  },

  grade: {
    kind: 'grade',
    title: 'Grades',
    singular: 'grade',
    subtitle: "The tenant's own grade vocabulary — what a grading gate counts pieces into",
    fields: [
      {
        key: 'Sequence',
        label: 'Sequence',
        type: 'number',
        required: true,
        precision: 0,
        hint: 'Display order on every gate and packing list. Two grades cannot share a number.',
      },
      {
        key: 'Code',
        label: 'Code',
        type: 'text',
        required: true,
        maxLength: 10,
        hint: 'Letters and digits only. Stored on every batch, output and stock label as-is — avoid changing it once in use.',
      },
      NAME,
      {
        key: 'IsSaleable',
        label: 'Saleable',
        type: 'bool',
        defaultValue: true,
        hint: 'Off for a reject grade — a batch line at a non-saleable grade can only Rework or Scrap, never Continue, Split or Exit.',
      },
      { key: 'Description', label: 'Description', type: 'text', maxLength: 300, inList: false },
      ACTIVE,
    ],
  },

  equipment: {
    kind: 'equipment',
    title: 'Equipment',
    singular: 'machine',
    subtitle: 'Drums, paddles and the machines a stage runs on',
    fields: [
      CODE,
      NAME,
      { key: 'Type', label: 'Type', type: 'select', required: true, options: EQUIPMENT_TYPES },
      {
        key: 'Capacity',
        label: 'Capacity',
        type: 'number',
        hint: 'The load this machine holds, in the unit beside it. Used to show how full a production order fills it.',
      },
      { key: 'CapacityUnit', label: 'Capacity unit', type: 'text', maxLength: 20 },
      {
        // NOT NULL server-side, and the database default (0.85) only applies
        // when the key is absent from the payload — this form always sends
        // it, so clearing the input would send null and come back as a raw
        // Newtonsoft conversion error rather than a validation message.
        // Required here catches that before the request goes out.
        key: 'MinFillRatio',
        label: 'Min fill ratio',
        type: 'number',
        required: true,
        hint: 'A production order below this share of capacity is flagged. 0.85 means 85%. Conventionally 0.85.',
      },
      {
        key: 'MaxFillRatio',
        label: 'Max fill ratio',
        type: 'number',
        required: true,
        hint: 'Conventionally 1.00.',
      },
      { key: 'CostingMethod', label: 'Costing method', type: 'select', options: EQUIPMENT_COSTING_METHODS, inList: false },
      {
        key: 'PowerRatingKW',
        label: 'Power rating (kW)',
        type: 'number',
        inList: false,
        hint: "Read by Sprint 11's power-based costing.",
      },
      {
        key: 'RatePerHour',
        label: 'Rate per hour',
        type: 'number',
        inList: false,
        hint: "Read by Sprint 11's simple-rate costing.",
      },
      {
        key: 'RatePerKWH',
        label: 'Rate per kWh',
        type: 'number',
        inList: false,
        hint: "Read by Sprint 11's power-based costing.",
      },
      ACTIVE,
    ],
  },

  exportprofile: {
    kind: 'exportprofile',
    title: 'Export Profile',
    singular: 'export profile',
    subtitle: "The tenant's export identity — one row for each financial year",
    fields: [
      {
        key: 'FinancialYear',
        label: 'Financial year',
        type: 'text',
        required: true,
        maxLength: 5,
        hint: 'Written as 26-27, meaning April 2026 to March 2027. One profile per year, and a consignment looks its year up by this exactly — any other form is never found.',
      },
      { key: 'IecNumber', label: 'IEC number', type: 'text', maxLength: 20 },
      {
        key: 'LutNumber',
        label: 'LUT number',
        type: 'text',
        maxLength: 40,
        hint: 'The Letter of Undertaking filed for this year. An export leaving without payment of IGST is refused until this and its date are both here.',
      },
      { key: 'LutDate', label: 'LUT date', type: 'date' },
      {
        key: 'AdCode',
        label: 'AD code',
        type: 'text',
        maxLength: 20,
        hint: 'Authorised Dealer code of the bank handling the export proceeds.',
      },
      { key: 'BankName', label: 'Bank', type: 'text', maxLength: 200, inList: false },
      { key: 'BankAccountNumber', label: 'Bank account number', type: 'text', maxLength: 40, inList: false },
      { key: 'Remarks', label: 'Remarks', type: 'text', maxLength: 500, inList: false },
      // No Active field: the table has no such column. A year's profile is
      // corrected, never retired.
    ],
  },

  customertolerance: {
    kind: 'customertolerance',
    title: 'Customer Tolerances',
    singular: 'customer tolerance',
    subtitle: 'What each customer has agreed to accept over or under the ordered figure',
    fields: [
      { key: 'CustomerId', label: 'Customer', type: 'customer', required: true },
      {
        key: 'MaterialState',
        label: 'Material state',
        type: 'select',
        required: true,
        options: TOLERANCE_MATERIAL_STATES,
        hint: 'The state the goods leave in. A tolerance applies only to consignments in this state.',
      },
      {
        key: 'Measure',
        label: 'Measure',
        type: 'select',
        required: true,
        options: TOLERANCE_MEASURES,
        hint: 'Quantity is pieces or weight, Measurement is area, Grade mix is the share of each grade in the consignment.',
      },
      {
        key: 'TolerancePct',
        label: 'Tolerance (%)',
        type: 'number',
        required: true,
        allowZero: true,
        defaultValue: 0,
        hint: 'How far over or under the ordered figure this customer accepts. 0 means no deviation at all. Leave the row off the list entirely rather than entering 0 if nothing was agreed — with no row, the article and then the tenant default answer instead.',
      },
      { key: 'Remarks', label: 'Remarks', type: 'text', maxLength: 500, inList: false },
      {
        key: 'IsActive',
        label: 'Active',
        type: 'bool',
        hint: 'Switch off a tolerance that no longer applies. It keeps its history and stops being used, and the customer can be given a new one for the same state and measure.',
      },
    ],
  },
};

export const MASTER_ORDER: TanneryMasterKind[] = [
  'hidetype',
  'stageconfig',
  'finishtype',
  'tanningmethod',
  'qctest',
  'grade',
  'equipment',
  'customertolerance',
  'exportprofile',
];
